import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle, FastForward, Loader2, Maximize, Minimize, Pause, Play, RefreshCw, Rewind,
  RotateCcw, RotateCw, Volume1, Volume2, VolumeX,
} from "lucide-react";
import AppButton from "./AppButton";
import VideoSlider from "./VideoSlider";
import cn from "./cn";
import useMediaState from "./useMediaState";
import useElementFullscreen from "./useElementFullscreen";
import {
  ARROW_SECONDS, DOUBLE_TAP_MS, HIDE_CONTROLS_MS, RESUME_TAIL_SECONDS, SEEK_STREAK_MS, SKIP_SECONDS, SPEEDS,
  browserStorage, clampTime, formatTime, isTypingTarget, knownDuration, nextSpeed, readPrefs, shortcutFor,
  speedLabel, spokenTime, tapZone, writePrefs,
} from "./videoPlayerRules";

const assignRef = (ref, value) => {
  if (typeof ref === "function") ref(value);
  else if (ref) ref.current = value;
};

const prefersTouch = () => {
  try {
    return !!window.matchMedia?.("(pointer: coarse)").matches;
  } catch {
    return false;
  }
};

const startPlaying = (v) => {
  const p = v?.play();
  if (p && p.catch) p.catch(() => {});
};

/** Is keyboard focus (not a tap) on something inside `el`? Keeps the controls up for keyboard users. */
const keyboardFocusInside = (el) => {
  try {
    return !!el?.querySelector(":focus-visible");
  } catch {
    return false;
  }
};

const BUTTON_SIZES = {
  md: "tw-h-10 tw-w-10 tw-bg-transparent hover:tw-bg-on-surface-dark/15",
  text: "tw-h-10 tw-min-w-10 tw-bg-transparent tw-px-2 tw-text-xs tw-font-semibold tw-tabular-nums hover:tw-bg-on-surface-dark/15",
  lg: "tw-h-11 tw-w-11 tw-bg-surface-dark/45 hover:tw-bg-surface-dark/60 sm:tw-h-12 sm:tw-w-12",
  xl: "tw-h-14 tw-w-14 tw-bg-surface-dark/55 hover:tw-bg-surface-dark/70 sm:tw-h-16 sm:tw-w-16",
};

/**
 * A control drawn over the picture. Not `IconButton`: AppButton's variants are
 * inks for a page surface, and these sit on video of any colour. The label is
 * required for the same reason it is there — it is the button's only name.
 */
const PlayerButton = React.forwardRef(({ label, size = "md", className, children, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label={label}
    title={label}
    className={cn(
      "tw-relative tw-inline-flex tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-p-0",
      "tw-text-on-surface-dark tw-outline-none tw-transition-colors focus-visible:tw-shadow-focus",
      BUTTON_SIZES[size],
      className,
    )}
    {...rest}
  >
    {children}
  </button>
));
PlayerButton.displayName = "PlayerButton";

/** ⟲ / ⟳ with the seconds written inside, the way every video app draws "back 10". */
const SkipGlyph = ({ back = false, size = 22 }) => {
  const Icon = back ? RotateCcw : RotateCw;
  return (
    <>
      <Icon size={size} aria-hidden="true" />
      <span
        aria-hidden="true"
        className={cn(
          "tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-pt-px tw-font-bold tw-leading-none",
          size >= 26 ? "tw-text-[0.625rem]" : "tw-text-[0.5rem]",
        )}
      >
        {SKIP_SECONDS}
      </span>
    </>
  );
};

const VolumeIcon = ({ volume, muted, size }) => {
  if (muted || volume === 0) return <VolumeX size={size} aria-hidden="true" />;
  if (volume < 0.5) return <Volume1 size={size} aria-hidden="true" />;
  return <Volume2 size={size} aria-hidden="true" />;
};

const SpeedPanel = React.forwardRef(({ rate, onPick, onClose }, ref) => {
  const innerRef = useRef(null);
  useEffect(() => {
    innerRef.current?.querySelector('[aria-pressed="true"]')?.focus();
  }, []);
  return (
    <div
      ref={(el) => {
        innerRef.current = el;
        assignRef(ref, el);
      }}
      role="group"
      aria-label="Playback speed"
      className="tw-absolute tw-bottom-[4.5rem] tw-right-2 tw-z-40 tw-max-h-[calc(100%-5rem)] tw-animate-panel-fade tw-overflow-y-auto tw-rounded tw-bg-surface-dark/95 tw-p-2.5 tw-shadow-popover"
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }}
    >
      <p className="tw-mb-2 tw-mt-0 tw-px-0.5 tw-text-xs tw-font-semibold tw-text-on-surface-dark/80">Playback speed</p>
      <div className="tw-grid tw-grid-cols-4 tw-gap-1.5">
        {SPEEDS.map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={s === rate}
            onClick={() => onPick(s)}
            className={cn(
              "tw-h-8 tw-min-w-[3rem] tw-cursor-pointer tw-rounded-sm tw-border-none tw-px-2 tw-text-xs tw-font-semibold tw-tabular-nums tw-outline-none focus-visible:tw-shadow-focus",
              s === rate
                ? "tw-bg-primary-solid tw-text-primary-solid-fg"
                : "tw-bg-on-surface-dark/10 tw-text-on-surface-dark hover:tw-bg-on-surface-dark/20",
            )}
          >
            {s === 1 ? "Normal" : speedLabel(s)}
          </button>
        ))}
      </div>
    </div>
  );
});
SpeedPanel.displayName = "SpeedPanel";

/**
 * VideoPlayer — the app's video player: our own controls on a bare <video>.
 *
 * The browser's controls are not used, for three reasons found on phones:
 * they differ per browser, they put a second play button on top of any of
 * ours, and anything laid over the picture (a watermark, a shield) blocks
 * their seek bar, so students could not move through a video without pausing
 * it first. These controls are one design everywhere, sized for a thumb.
 *
 *   tap               show / hide the controls (mouse: click plays / pauses)
 *   double tap        left 40 % back 10 s, right 40 % forward 10 s — keep
 *                     tapping to keep going; the middle plays / pauses
 *                     (mouse: double-click is full screen)
 *   seek bar          drag while it plays; the time follows the finger
 *   keys              Space / K play · ← → 5 s · J L 10 s · ↑ ↓ volume ·
 *                     M mute · F full screen · 0–9 jump · < > speed
 *
 * Speed and volume are remembered for the viewer (this browser only). Full
 * screen is the player's BOX, never the bare video, so whatever is drawn over
 * the picture (`overlay`, `children`) stays — see `useElementFullscreen`.
 *
 * Hardening it brings by itself: no native controls (so no download / cast /
 * picture-in-picture menus), `playsInline` (iPhone otherwise plays in its own
 * full screen, without our overlays), PiP and remote playback disabled, and
 * the video element takes no pointer events, so a long press or right-click
 * never reaches it. Content protection beyond that is the caller's —
 * `Common/ProtectedVideoPlayer` adds the watermark and `useVideoProtection`.
 *
 * Expired links: when the stream fails after playback started and `onExpired`
 * is given, the player asks for a fresh link once, and on the new `src` puts
 * the playhead back where it was and carries on playing. Otherwise (or if the
 * fresh link fails too) it shows "Try again".
 *
 * The box needs a size — fills its parent by default (an aspect-ratio box);
 * pass `className` to size it yourself (e.g. "tw-aspect-video tw-w-full").
 *
 * Props:
 *   src, poster, autoPlay, preload ("metadata")
 *   title         — names the player for assistive tech
 *   startAt       — seconds to resume from, applied ONCE per `mediaKey` (and
 *                   never onto the last 5 s); a seek by the viewer cancels it
 *   mediaKey      — what `startAt` belongs to (the video's id)
 *   durationHint  — seconds, shown until the file reports its own length
 *   onExpired(t)  — fetch a fresh `src`; `t` is where playback stopped
 *   overlay       — drawn over the picture, under the controls (a watermark)
 *   children      — drawn over everything
 *   rootRef       — the player's box (for a hook that watches it)
 *   ref           — the <video> element
 *   onPlay, onPause, onEnded, onTimeUpdate, onLoadedData, onLoadedMetadata, onError
 */
const VideoPlayer = React.forwardRef(
  (
    {
      src,
      poster,
      title,
      autoPlay = false,
      preload = "metadata",
      startAt = 0,
      mediaKey = "video",
      durationHint = 0,
      onExpired,
      overlay,
      children,
      rootRef,
      className,
      onPlay,
      onPause,
      onEnded,
      onTimeUpdate,
      onLoadedData,
      onLoadedMetadata,
      onError,
    },
    ref,
  ) => {
    const rootElRef = useRef(null);
    const videoElRef = useRef(null);
    const layerRef = useRef(null);
    const speedButtonRef = useRef(null);
    const speedPanelRef = useRef(null);
    const [video, setVideo] = useState(null);

    const setRoot = useCallback((el) => {
      rootElRef.current = el;
      assignRef(rootRef, el);
    }, [rootRef]);
    const setVideoRef = useCallback((el) => {
      videoElRef.current = el;
      setVideo(el);
      assignRef(ref, el);
    }, [ref]);

    const media = useMediaState(video);
    const duration = knownDuration(media.duration, durationHint);

    const [touchUi, setTouchUi] = useState(prefersTouch);
    const [awake, setAwake] = useState(true);
    const awakeRef = useRef(true);
    const [speedOpen, setSpeedOpen] = useState(false);
    const [scrubTime, setScrubTime] = useState(null);
    const [flash, setFlash] = useState(null);
    const [recovering, setRecovering] = useState(false);

    const timers = useRef({});
    const latest = useRef({});
    const appliedKeyRef = useRef(null);
    const loadKeyRef = useRef(null);
    const restoreRef = useRef(null);
    const intendRef = useRef(false);
    const autoRetriedRef = useRef(null);
    const downRef = useRef(null);
    const lastTapRef = useRef({ t: 0 });
    const streakRef = useRef(null);
    const swallowTapRef = useRef(false);

    const clearTimer = (name) => {
      clearTimeout(timers.current[name]);
      timers.current[name] = null;
    };
    useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

    // ── Controls: awake on any touch or mouse move, asleep 3 s later while playing ──
    const setAwakeBoth = useCallback((v) => {
      awakeRef.current = v;
      setAwake(v);
    }, []);
    const wake = useCallback(() => {
      setAwakeBoth(true);
      clearTimer("hide");
      const hide = () => {
        if (keyboardFocusInside(rootElRef.current)) timers.current.hide = setTimeout(hide, HIDE_CONTROLS_MS);
        else setAwakeBoth(false);
      };
      timers.current.hide = setTimeout(hide, HIDE_CONTROLS_MS);
    }, [setAwakeBoth]);
    const sleep = useCallback(() => {
      clearTimer("hide");
      setAwakeBoth(false);
    }, [setAwakeBoth]);

    // Starting playback leaves the controls up for a moment, then fades them.
    useEffect(() => {
      if (!media.paused) wake();
    }, [media.paused, wake]);

    // ── Resume point ──
    const applyStart = useCallback(() => {
      const v = videoElRef.current;
      const { startAt: at, mediaKey: key } = latest.current;
      if (!v || !(at > 0) || v.readyState < 1) return;
      // Once per video, only on a file loaded FOR this video, and never over a restore.
      if (appliedKeyRef.current === key || loadKeyRef.current !== key || restoreRef.current) return;
      appliedKeyRef.current = key;
      const d = v.duration;
      if (Number.isFinite(d) && at < d - RESUME_TAIL_SECONDS) {
        try {
          v.currentTime = at;
        } catch {
          /* not seekable yet */
        }
      }
    }, []);

    useEffect(() => {
      latest.current = { startAt, mediaKey, src, onExpired, durationHint };
      applyStart();
    }, [startAt, mediaKey, src, onExpired, durationHint, applyStart]);

    // ── The element: preferences, hardening, resume, expired-link recovery ──
    useEffect(() => {
      if (!video) return undefined;
      const { rate, volume } = readPrefs(browserStorage());
      video.defaultPlaybackRate = rate;
      video.playbackRate = rate;
      video.volume = volume;
      try {
        video.disableRemotePlayback = true;
      } catch {
        /* not supported */
      }
      loadKeyRef.current = latest.current.mediaKey;

      const onLoadStart = () => {
        loadKeyRef.current = latest.current.mediaKey;
      };
      // The resume point is used up once the viewer is watching: a page whose
      // `startAt` follows the saved progress must not pull a playing video back.
      const spendStart = () => {
        if (loadKeyRef.current === latest.current.mediaKey) appliedKeyRef.current = latest.current.mediaKey;
      };
      const onMeta = () => {
        const r = restoreRef.current;
        if (!r) {
          applyStart();
          // Play pressed before the length was known (iPhone loads nothing until then).
          if (intendRef.current) spendStart();
          return;
        }
        restoreRef.current = null;
        appliedKeyRef.current = latest.current.mediaKey;
        clearTimer("recover");
        setRecovering(false);
        if (r.time > 0) {
          try {
            video.currentTime = r.time;
          } catch {
            /* not seekable yet */
          }
        }
        if (r.play) startPlaying(video);
      };
      const onPlaying = () => {
        intendRef.current = true;
        if (video.readyState >= 1) {
          applyStart();
          spendStart();
        }
      };
      const onStopped = () => {
        intendRef.current = false;
      };
      const onFailed = () => {
        const t = video.currentTime || 0;
        const { onExpired: expired, src: current } = latest.current;
        // Played, then failed: almost always the signed link expiring. Ask for a fresh one, once per link.
        if (expired && t > 0 && autoRetriedRef.current !== current) {
          autoRetriedRef.current = current;
          restoreRef.current = { time: t, play: intendRef.current };
          setRecovering(true);
          clearTimer("recover");
          timers.current.recover = setTimeout(() => {
            restoreRef.current = null;
            setRecovering(false);
          }, 15000);
          expired(t);
          return;
        }
        restoreRef.current = null;
        clearTimer("recover");
        setRecovering(false);
      };

      video.addEventListener("loadstart", onLoadStart);
      video.addEventListener("loadedmetadata", onMeta);
      video.addEventListener("play", onPlaying);
      video.addEventListener("pause", onStopped);
      video.addEventListener("ended", onStopped);
      video.addEventListener("error", onFailed);
      return () => {
        video.removeEventListener("loadstart", onLoadStart);
        video.removeEventListener("loadedmetadata", onMeta);
        video.removeEventListener("play", onPlaying);
        video.removeEventListener("pause", onStopped);
        video.removeEventListener("ended", onStopped);
        video.removeEventListener("error", onFailed);
      };
    }, [video, applyStart]);

    // A phone turns sideways for a landscape video (where the browser lets a page do that).
    const wantsLandscape = useCallback(() => {
      const v = videoElRef.current;
      return prefersTouch() && !!v && v.videoWidth >= v.videoHeight;
    }, []);
    const fs = useElementFullscreen(rootElRef, { wantsLandscape });
    const toggleFullscreen = fs.toggle;

    // ── Actions ──
    const togglePlay = useCallback(() => {
      const v = videoElRef.current;
      if (!v) return;
      if (v.ended) {
        v.currentTime = 0;
        startPlaying(v);
      } else if (v.paused) startPlaying(v);
      else v.pause();
    }, []);

    const seekTo = useCallback((t) => {
      const v = videoElRef.current;
      if (!v) return;
      // The viewer chose a position: a resume point that has not landed yet must not override it.
      appliedKeyRef.current = latest.current.mediaKey;
      try {
        v.currentTime = clampTime(t, knownDuration(v.duration, latest.current.durationHint));
      } catch {
        /* not seekable yet */
      }
    }, []);

    const skip = useCallback((by) => {
      const v = videoElRef.current;
      if (v) seekTo(v.currentTime + by);
    }, [seekTo]);

    const setVolume = useCallback((value) => {
      const v = videoElRef.current;
      if (!v) return;
      const next = Math.min(1, Math.max(0, value));
      v.volume = next;
      v.muted = next === 0;
      if (next > 0) {
        const storage = browserStorage();
        writePrefs(storage, { ...readPrefs(storage), volume: next });
      }
    }, []);

    const toggleMute = useCallback(() => {
      const v = videoElRef.current;
      if (!v) return;
      if (v.muted || v.volume === 0) {
        if (v.volume === 0) v.volume = 0.5;
        v.muted = false;
      } else {
        v.muted = true;
      }
    }, []);

    const setRate = useCallback((rate) => {
      const v = videoElRef.current;
      if (!v) return;
      v.defaultPlaybackRate = rate;
      v.playbackRate = rate;
      const storage = browserStorage();
      writePrefs(storage, { ...readPrefs(storage), rate });
    }, []);

    const retry = () => {
      const v = videoElRef.current;
      if (!v) return;
      restoreRef.current = { time: v.currentTime || 0, play: true };
      setRecovering(true);
      clearTimer("recover");
      timers.current.recover = setTimeout(() => {
        restoreRef.current = null;
        setRecovering(false);
      }, 15000);
      if (onExpired) onExpired(v.currentTime || 0);
      // Same link: setting src, even to the value it had, runs the load again.
      else v.setAttribute("src", src);
    };

    const closeSpeed = useCallback((refocus) => {
      setSpeedOpen(false);
      if (refocus) speedButtonRef.current?.focus();
    }, []);

    const runShortcut = useCallback((action) => {
      const v = videoElRef.current;
      if (!v) return;
      if (action.type === "toggle") togglePlay();
      else if (action.type === "seekBy") skip(action.by);
      else if (action.type === "seekTo") seekTo(action.fraction * knownDuration(v.duration, latest.current.durationHint));
      else if (action.type === "volumeBy") setVolume((v.muted ? 0 : v.volume) + action.by);
      else if (action.type === "mute") toggleMute();
      else if (action.type === "fullscreen") toggleFullscreen();
      else if (action.type === "rate") setRate(nextSpeed(v.playbackRate, action.direction));
      wake();
    }, [togglePlay, skip, seekTo, setVolume, toggleMute, toggleFullscreen, setRate, wake]);

    // ── Keyboard: inside the player, and on the page while nothing else has focus ──
    const onRootKeyDown = (e) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "Escape" && speedOpen) {
        e.preventDefault();
        closeSpeed(true);
        return;
      }
      if (isTypingTarget(e.target)) return;
      // Space / Enter on a focused control is that control's own click.
      if ((e.key === " " || e.key === "Enter") && e.target.closest?.("button, a, [role='slider']")) return;
      const action = shortcutFor(e.key, { inside: true });
      if (!action) return;
      e.preventDefault();
      runShortcut(action);
    };

    useEffect(() => {
      const onKey = (e) => {
        if (e.target !== document.body && e.target !== document.documentElement) return;
        if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return;
        const action = shortcutFor(e.key, { inside: false });
        if (!action) return;
        e.preventDefault();
        runShortcut(action);
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    }, [runShortcut]);

    // ── Taps and clicks on the picture ──
    const seekStreak = (zone) => {
      skip(zone === "back" ? -SKIP_SECONDS : SKIP_SECONDS);
      const now = Date.now();
      const prev = streakRef.current;
      const total = prev && prev.zone === zone && now < prev.until ? prev.total + SKIP_SECONDS : SKIP_SECONDS;
      streakRef.current = { zone, until: now + SEEK_STREAK_MS, total };
      setFlash({ zone, seconds: total, key: now });
      clearTimer("flash");
      timers.current.flash = setTimeout(() => {
        setFlash(null);
        streakRef.current = null;
      }, SEEK_STREAK_MS);
    };

    const onSingleTap = () => {
      const v = videoElRef.current;
      if (v && !v.paused && awakeRef.current) sleep();
      else wake();
    };

    const onLayerPointerDown = (e) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      downRef.current = { x: e.clientX, y: e.clientY, t: Date.now(), type: e.pointerType };
    };

    const onLayerPointerUp = (e) => {
      const down = downRef.current;
      downRef.current = null;
      if (!down) return;
      if (swallowTapRef.current) {
        swallowTapRef.current = false;
        return;
      }
      const now = Date.now();
      // A drag or a long press is not a tap.
      if (Math.hypot(e.clientX - down.x, e.clientY - down.y) > 12 || now - down.t > 600) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const zone = tapZone(e.clientX - rect.left, rect.width);
      const last = lastTapRef.current;
      const isDouble = now - last.t < DOUBLE_TAP_MS && last.type === down.type;

      if (down.type === "mouse") {
        togglePlay();
        if (isDouble) {
          // The first click already toggled; this one undoes it, so a double-click only changes the screen.
          toggleFullscreen();
          lastTapRef.current = { t: 0 };
        } else {
          lastTapRef.current = { t: now, type: "mouse" };
        }
        return;
      }

      const streak = streakRef.current;
      if (zone !== "centre" && streak && streak.zone === zone && now < streak.until) {
        seekStreak(zone);
        return;
      }
      if (isDouble) {
        clearTimer("tap");
        lastTapRef.current = { t: 0 };
        if (zone === "centre") togglePlay();
        else seekStreak(zone);
        return;
      }
      lastTapRef.current = { t: now, type: down.type };
      clearTimer("tap");
      timers.current.tap = setTimeout(onSingleTap, DOUBLE_TAP_MS);
    };

    const onRootPointerDownCapture = (e) => {
      setTouchUi(e.pointerType !== "mouse");
      if (speedOpen && !speedPanelRef.current?.contains(e.target) && !speedButtonRef.current?.contains(e.target)) {
        closeSpeed(false);
        if (e.target === layerRef.current) swallowTapRef.current = true;
      }
      // Touching any control keeps the controls up.
      if (e.target !== layerRef.current) wake();
    };

    // ── What shows ──
    const failed = !!media.error && !recovering;
    // `waiting`, not `seeking`: a seek inside what is already loaded takes milliseconds, and a spinner for it is a flicker.
    const loading = recovering || (!media.paused && !failed && media.waiting);
    const showControls = awake || media.paused || media.ended || scrubTime !== null || speedOpen || failed;
    const centreVisible = loading || (!failed && !speedOpen && (touchUi ? showControls : media.paused || media.ended));
    const showSkips = touchUi && showControls && media.hasPlayed && !media.ended && !failed && !loading;
    const centreLabel = media.ended ? "Replay" : media.paused ? "Play" : "Pause";

    return (
      <div
        ref={setRoot}
        role="region"
        aria-label={title ? `Video: ${title}` : "Video player"}
        tabIndex={-1}
        className={cn(
          fs.isPseudo ? "tw-fixed tw-inset-0 tw-z-modal tw-h-dvh tw-w-screen" : cn("tw-relative", className ?? "tw-h-full tw-w-full"),
          "tw-overflow-hidden tw-bg-[var(--media-mat)] tw-outline-none tw-select-none [-webkit-touch-callout:none]",
          !showControls && "tw-cursor-none",
        )}
        onKeyDown={onRootKeyDown}
        onPointerDownCapture={onRootPointerDownCapture}
        onPointerMove={(e) => e.pointerType === "mouse" && wake()}
        onPointerLeave={(e) => e.pointerType === "mouse" && !media.paused && sleep()}
        onFocus={(e) => e.target !== rootElRef.current && wake()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <video
          ref={setVideoRef}
          src={src || undefined}
          poster={poster || undefined}
          autoPlay={autoPlay}
          preload={preload}
          playsInline
          webkit-playsinline="true"
          x-webkit-airplay="deny"
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
          draggable={false}
          tabIndex={-1}
          className="tw-pointer-events-none tw-absolute tw-inset-0 tw-h-full tw-w-full tw-select-none tw-object-contain"
          onPlay={onPlay}
          onPause={onPause}
          onEnded={onEnded}
          onTimeUpdate={onTimeUpdate}
          onLoadedData={onLoadedData}
          onLoadedMetadata={onLoadedMetadata}
          onError={onError}
        />

        {overlay}

        <div
          ref={layerRef}
          aria-hidden="true"
          className="tw-absolute tw-inset-0 tw-z-10 tw-touch-manipulation"
          onPointerDown={onLayerPointerDown}
          onPointerUp={onLayerPointerUp}
          onPointerCancel={() => {
            downRef.current = null;
          }}
        />

        {flash && (
          <div
            key={flash.key}
            aria-hidden="true"
            className={cn(
              "tw-pointer-events-none tw-absolute tw-inset-y-0 tw-z-20 tw-flex tw-w-2/5 tw-items-center tw-justify-center tw-bg-on-surface-dark/10",
              flash.zone === "back" ? "tw-left-0 tw-rounded-r-[50%]" : "tw-right-0 tw-rounded-l-[50%]",
            )}
          >
            <span className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-on-surface-dark">
              {flash.zone === "back" ? <Rewind size={22} fill="currentColor" /> : <FastForward size={22} fill="currentColor" />}
              {flash.seconds} seconds
            </span>
          </div>
        )}

        {centreVisible && (
          // Centred in the picture ABOVE the control bar, so on a phone-sized
          // player the big button never sits on the seek bar's touch area.
          <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-10 tw-top-0 tw-z-20 tw-flex tw-animate-panel-fade tw-items-center tw-justify-center tw-gap-8 sm:tw-gap-14">
            {showSkips && (
              <PlayerButton size="lg" className="tw-pointer-events-auto" label={`Back ${SKIP_SECONDS} seconds`} onClick={() => skip(-SKIP_SECONDS)}>
                <SkipGlyph back size={26} />
              </PlayerButton>
            )}
            {loading ? (
              <span role="status" className="tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-text-on-surface-dark">
                <Loader2 size={44} className="tw-animate-spin" aria-hidden="true" />
                <span className="tw-sr-only">Loading</span>
              </span>
            ) : (
              <PlayerButton size="xl" className="tw-pointer-events-auto" label={centreLabel} onClick={togglePlay}>
                {media.ended && <RotateCcw size={30} aria-hidden="true" />}
                {!media.ended && media.paused && <Play size={30} fill="currentColor" className="tw-ml-1" aria-hidden="true" />}
                {!media.ended && !media.paused && <Pause size={30} fill="currentColor" aria-hidden="true" />}
              </PlayerButton>
            )}
            {showSkips && (
              <PlayerButton size="lg" className="tw-pointer-events-auto" label={`Forward ${SKIP_SECONDS} seconds`} onClick={() => skip(SKIP_SECONDS)}>
                <SkipGlyph size={26} />
              </PlayerButton>
            )}
          </div>
        )}

        <div
          className={cn(
            "tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-z-30 tw-bg-gradient-to-t tw-from-surface-dark/85 tw-via-surface-dark/40 tw-to-transparent tw-pt-8 tw-transition-opacity tw-duration-200 sm:tw-pt-12",
            fs.isFullscreen
              ? "tw-pb-[max(0.5rem,env(safe-area-inset-bottom))] tw-pl-[max(0.75rem,env(safe-area-inset-left))] tw-pr-[max(0.75rem,env(safe-area-inset-right))]"
              : "tw-px-2 tw-pb-1 sm:tw-px-3",
            showControls ? "tw-opacity-100" : "tw-opacity-0",
          )}
        >
          <div className={cn(showControls && "tw-pointer-events-auto")}>
            <VideoSlider
              label="Seek"
              value={media.currentTime}
              max={duration}
              buffered={media.buffered}
              step={ARROW_SECONDS}
              bigStep={SKIP_SECONDS}
              onCommit={seekTo}
              onScrub={setScrubTime}
              valueText={(v) => `${spokenTime(v)} of ${spokenTime(duration)}`}
              preview={(v) => formatTime(v, duration)}
              className="tw-mx-1"
            />
            <div className="tw-flex tw-items-center tw-gap-0.5 sm:tw-gap-1">
              {!touchUi && (
                <>
                  <PlayerButton label={media.paused ? "Play (k)" : "Pause (k)"} onClick={togglePlay}>
                    {media.paused ? <Play size={20} fill="currentColor" aria-hidden="true" /> : <Pause size={20} fill="currentColor" aria-hidden="true" />}
                  </PlayerButton>
                  <PlayerButton label={`Back ${SKIP_SECONDS} seconds (j)`} onClick={() => skip(-SKIP_SECONDS)}>
                    <SkipGlyph back size={22} />
                  </PlayerButton>
                  <PlayerButton label={`Forward ${SKIP_SECONDS} seconds (l)`} onClick={() => skip(SKIP_SECONDS)}>
                    <SkipGlyph size={22} />
                  </PlayerButton>
                </>
              )}
              <div className="tw-group/volume tw-flex tw-items-center">
                <PlayerButton label={media.muted || media.volume === 0 ? "Unmute (m)" : "Mute (m)"} onClick={toggleMute}>
                  <VolumeIcon volume={media.volume} muted={media.muted} size={20} />
                </PlayerButton>
                {!touchUi && (
                  <div className="tw-w-0 tw-overflow-hidden tw-py-1 tw-transition-[width] tw-duration-200 group-hover/volume:tw-w-20 group-focus-within/volume:tw-w-20">
                    <VideoSlider
                      label="Volume"
                      value={media.muted ? 0 : media.volume}
                      max={1}
                      step={0.05}
                      bigStep={0.2}
                      live
                      onCommit={setVolume}
                      valueText={(v) => `${Math.round(v * 100)}%`}
                      className="tw-mx-2 tw-w-16"
                    />
                  </div>
                )}
              </div>
              <span className="tw-ml-1 tw-whitespace-nowrap tw-text-xs tw-font-medium tw-tabular-nums tw-text-on-surface-dark sm:tw-text-[0.8125rem]">
                {formatTime(scrubTime ?? media.currentTime, duration)}
                {" / "}
                {duration ? formatTime(duration) : "--:--"}
              </span>
              <span className="tw-flex-1" />
              <PlayerButton
                ref={speedButtonRef}
                size="text"
                label={`Playback speed: ${speedLabel(media.rate)}`}
                aria-expanded={speedOpen}
                aria-haspopup="true"
                onClick={() => (speedOpen ? closeSpeed(false) : setSpeedOpen(true))}
              >
                {speedLabel(media.rate)}
              </PlayerButton>
              <PlayerButton label={fs.isFullscreen ? "Exit full screen (f)" : "Full screen (f)"} onClick={toggleFullscreen}>
                {fs.isFullscreen ? <Minimize size={20} aria-hidden="true" /> : <Maximize size={20} aria-hidden="true" />}
              </PlayerButton>
            </div>
          </div>
        </div>

        {speedOpen && (
          <SpeedPanel
            ref={speedPanelRef}
            rate={media.rate}
            onPick={(rate) => {
              setRate(rate);
              closeSpeed(true);
              wake();
            }}
            onClose={() => closeSpeed(true)}
          />
        )}

        {failed && (
          <div
            role="alert"
            className="tw-absolute tw-inset-0 tw-z-40 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-bg-surface-dark/85 tw-p-4 tw-text-center tw-text-on-surface-dark"
          >
            <AlertTriangle size={28} className="tw-text-warning" aria-hidden="true" />
            <p className="tw-m-0 tw-text-sm tw-font-semibold">This video couldn&apos;t be played.</p>
            <p className="tw-m-0 tw-text-xs tw-text-on-surface-dark/75">Check your internet connection, then try again.</p>
            <AppButton size="sm" className="tw-mt-1" onClick={retry}>
              <RefreshCw size={14} aria-hidden="true" /> Try again
            </AppButton>
          </div>
        )}

        {children}
      </div>
    );
  },
);

VideoPlayer.displayName = "VideoPlayer";
export default VideoPlayer;
